import React, {useEffect, useId, useRef, useState} from 'react';
import {ArrowUpRight, Plus, X} from 'lucide-react';
import Modal from './components/Modal.jsx';
import './discussion-to-poll.css';

const firstLine = text => String(text || '').trim().split(/\r?\n/)[0].replace(/^譯名[：:]\s*/, '').slice(0, 100);
const normalizeOption = text => text.trim().toLocaleLowerCase().replace(/\s+/g, ' ');

// This is a proposal, not a destructive conversion: the source stays in its
// thread and the server records its author/version separately from the proposer.
export default function DiscussionToPoll({source, root, term, api, onRefresh, onClose, onComplete}) {
  const fieldId = useId();
  const apiRef = useRef(api);
  apiRef.current = api;
  const mounted = useRef(true);
  const pending = useRef(false);
  const request = useRef(null);
  const errorRef = useRef(null);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);
  const [destination, setDestination] = useState('');
  const [optionChoice, setOptionChoice] = useState('');
  const [refreshNotice, setRefreshNotice] = useState('');
  const [name, setName] = useState(() => source.nameProposal?.name || source.pollContext?.optionName || firstLine(source.body));
  const [reason, setReason] = useState(() => String(source.body || '').slice(0, 2000));
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState(() => String(source.body || '').slice(0, 2000));
  const [options, setOptions] = useState(() => [firstLine(source.body), '']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setLoadError('');
    apiRef.current('polls?term=' + encodeURIComponent(term.id)).then(data => {
      if (!current) return;
      if (!Array.isArray(data?.items)) throw Error('議題清單格式不正確，請重新讀取。');
      setPolls(data.items.filter(poll => poll.kind === 'poll' && poll.status === 'open'));
    }).catch(cause => { if (current) setLoadError(cause.message || '無法讀取議題。'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [term.id, reload]);
  useEffect(() => {
    if (error) errorRef.current?.focus({preventScroll: true});
  }, [error]);

  const selectedPoll = polls.find(poll => destination === 'poll:' + poll.id);
  const isNew = destination === 'new';
  const isNames = destination === 'names';
  const selectedOption = selectedPoll?.options?.find(option => option.id === optionChoice);
  const isNewOption = Boolean(selectedPoll) && optionChoice === 'new';
  const ready = isNew || isNames || isNewOption || Boolean(selectedOption);
  const successTitle = result?.target?.kind === 'names' ? (result.existing ? '已連結投票項目' : '譯名已提出') : result?.existing ? '已連結投票項目' : result?.operation === 'new-poll' ? '議題已提出' : '選項已新增';
  const author = source.author?.name || '未具名成員';
  const rootAuthor = root?.id !== source.id ? root?.author?.name : '';
  const sourceVersion = source.version || 1;
  function close() { if (!pending.current) onClose(); }
  function changeDestination(value) { setDestination(value); setOptionChoice(''); setError(''); }

  async function submit(event) {
    event.preventDefault();
    if (pending.current || result) return;
    let target;
    if (isNew) {
      const names = options.map(option => option.trim());
      if (names.length < 2 || names.some(option => !option)) {
        setError('請填寫至少兩個選項。');
        return;
      }
      if (new Set(names.map(normalizeOption)).size !== names.length) {
        setError('選項文字重複，請調整後再提出。');
        return;
      }
      target = {kind: 'new-poll', question: question.trim(), description: description.trim(), options: names};
    } else if (isNames) {
      target = {kind: 'names', name: name.trim(), reason: reason.trim()};
    } else if (selectedPoll && selectedOption) {
      target = {kind: 'poll-option', pollId: selectedPoll.id, revision: selectedPoll.revision, optionId: selectedOption.id};
    } else if (isNewOption) {
      target = {kind: 'poll-option', pollId: selectedPoll.id, revision: selectedPoll.revision, name: name.trim(), reason: reason.trim()};
    } else {
      setError(selectedPoll ? '請選擇要連結的投票項目，或新增選項。' : '請先選擇譯名或進行中的議題。若議題已結束，可以重新讀取或新開議題。');
      return;
    }
    const payload = {commentVersion: sourceVersion, target};
    const signature = JSON.stringify(payload);
    if (request.current?.signature !== signature) request.current = {signature, key: crypto.randomUUID()};
    pending.current = true;
    setBusy(true);
    setError('');
    let saved;
    try {
      saved = await apiRef.current('comments/' + encodeURIComponent(source.id) + '/propose-vote', 'POST', {
        ...payload, requestKey: request.current.key
      });
    } catch (cause) {
      if (mounted.current) setError(cause.message || '尚未提出，請確認後重試。');
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
    if (!saved || !mounted.current) return;
    setResult({...saved, operation: target.kind});
    // The write already succeeded. A refresh failure must never offer a second
    // submission or turn a successful proposal into a misleading write error.
    try {
      const fresh = await onRefresh?.();
      if (mounted.current && (fresh === false || fresh === null)) setRefreshNotice('已儲存，畫面資料暫時未更新。可直接查看投票，不需再次送出。');
    } catch {
      if (mounted.current) setRefreshNotice('已儲存，畫面資料暫時未更新。可直接查看投票，不需再次送出。');
    }
  }

  return <Modal title={result ? successTitle : '連結投票項目'} className={'discussion-vote-dialog' + (busy ? ' is-submitting' : '')} onClose={close} closeDisabled={busy}>
    <form className="discussion-vote-form" onSubmit={submit} aria-busy={busy}>
      <details className="discussion-vote-source">
        <summary>源自 {author} 的留言<span>第 {sourceVersion} 版</span></summary>
        {rootAuthor && <p>{rootAuthor} 發起的討論串</p>}
        <blockquote>{source.body}</blockquote>
      </details>

      {result ? <div className="discussion-vote-success" role="status">
        <p>{result.existing ? (result.target?.kind === 'names' ? '已連結到既有譯名，原討論與作者保留。' : '已連結到這個投票選項，選項內容與作者不變。') : result.target?.kind === 'names' ? '新譯名已提出，原討論與作者保留。' : result.operation === 'new-poll' ? '新議題已提出，原討論與留言保留。' : '已新增投票選項，原討論與作者保留。'}</p>
        {refreshNotice && <p className="discussion-vote-hint">{refreshNotice}</p>}
        <div className="discussion-vote-actions">
          <button type="button" onClick={onClose}>關閉</button>
          <button type="button" className="discussion-vote-submit" onClick={() => onComplete?.(result)}>查看投票<ArrowUpRight size={16}/></button>
        </div>
      </div> : <>
        <fieldset disabled={busy}>
          <label htmlFor={fieldId + '-destination'}>加入哪個議題</label>
          <select autoFocus id={fieldId + '-destination'} required value={destination} onChange={event => changeDestination(event.target.value)}>
            <option value="">{loading ? '正在讀取議題…' : '選擇議題'}</option>
            <option value="names">新增譯名或別名</option>
            {polls.length > 0 && <optgroup label="進行中的議題">{polls.map(poll => <option key={poll.id} value={'poll:' + poll.id}>{poll.question}</option>)}</optgroup>}
            <option value="new">新開議題…</option>
          </select>
          {loadError && <div className="discussion-vote-load-error" role="status"><p>{loadError}</p><button type="button" onClick={() => setReload(value => value + 1)}>重新讀取議題</button></div>}

          {selectedPoll && <div className="discussion-vote-fields">
            <label htmlFor={fieldId + '-target-option'}>投票項目</label>
            <select id={fieldId + '-target-option'} required value={optionChoice} onChange={event => {setOptionChoice(event.target.value); setError('');}}>
              <option value="">選擇現有選項或新增</option>
              {(selectedPoll.options || []).length > 0 && <optgroup label="現有投票選項">{selectedPoll.options.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}</optgroup>}
              <option value="new">新增投票選項…</option>
            </select>
            {selectedOption && <section className="discussion-vote-existing-option" aria-label="將連結的投票選項">
              <h3>{selectedOption.name}</h3>
              <p>{selectedOption.author?.name || '未具名成員'} 提出</p>
              {selectedOption.reason && <p className="discussion-vote-existing-reason">{selectedOption.reason}</p>}
            </section>}
          </div>}

          {destination && (isNew ? <div className="discussion-vote-fields">
            <label htmlFor={fieldId + '-question'}>問題</label>
            <input id={fieldId + '-question'} required maxLength={120} value={question} onChange={event => setQuestion(event.target.value)} placeholder="這次要一起決定什麼？"/>
            <div className="discussion-vote-options">
              {options.map((option, index) => <div key={index}>
                <label htmlFor={fieldId + '-option-' + index}>選項 {index + 1}</label>
                <div className="discussion-vote-option-field"><input id={fieldId + '-option-' + index} required maxLength={100} value={option} onChange={event => setOptions(old => old.map((value, position) => position === index ? event.target.value : value))}/>
                  {options.length > 2 && <button type="button" aria-label={'移除選項 ' + (index + 1)} onClick={() => setOptions(old => old.filter((_, position) => position !== index))}><X size={16}/></button>}
                </div>
              </div>)}
              <button type="button" className="discussion-vote-add-option" disabled={options.length >= 20} onClick={() => setOptions(old => [...old, ''])}><Plus size={16}/>新增選項</button>
            </div>
            <details className="discussion-vote-background"><summary>背景或來源<span>選填</span></summary><label className="sr-only" htmlFor={fieldId + '-description'}>背景或來源</label><textarea id={fieldId + '-description'} rows={3} maxLength={2000} value={description} onChange={event => setDescription(event.target.value)}/></details>
          </div> : (isNames || isNewOption) && <div className="discussion-vote-fields">
            <label htmlFor={fieldId + '-name'}>{isNames ? '譯名或別名' : '選項文字'}</label>
            <input id={fieldId + '-name'} required maxLength={100} value={name} onChange={event => setName(event.target.value)} placeholder={isNames ? '輸入要提案的名稱' : '用一句話表達這個選項'}/>
            <label htmlFor={fieldId + '-reason'}>理由<span>選填</span></label>
            <textarea id={fieldId + '-reason'} rows={3} maxLength={2000} value={reason} onChange={event => setReason(event.target.value)}/>
          </div>)}
        </fieldset>
        {ready && <p className="discussion-vote-hint">{isNew ? '請確認問題與選項，提出後無法修改文字。' : selectedOption ? '只連結這則討論，不修改選項、不代替任何人投票。' : isNames ? '建立可投票的譯名候選，來源留言與作者會一併保留。' : '新增可投票的選項，來源留言與作者會一併保留。'}</p>}
        {error && <div className="discussion-vote-error" role="alert" tabIndex={-1} ref={errorRef}><p>{error}</p><button type="button" disabled={busy || loading} onClick={() => setReload(value => value + 1)}>重新讀取議題</button></div>}
        <div className="discussion-vote-actions">
          <button type="button" disabled={busy} onClick={close}>取消</button>
          <button className="discussion-vote-submit" type="submit" disabled={busy || !ready}>{busy ? '正在處理…' : isNew ? '提出新議題' : isNames ? '提出新譯名' : selectedOption ? '連結這個選項' : '新增投票選項'}</button>
        </div>
      </>}
    </form>
  </Modal>;
}
